/**
 * /admin/omie/audit-log — registro de tudo que o IGM tentou gravar no Omie
 * (R-IMPORT 3 A1.9 / Camada 4). Só leitura, paginado. Filtros: modo e
 * situação. A reversão de lançamentos `igm_v2_*` entra em A1.10+.
 *
 * FASE-C (redesign): `<table>` cru dentro de `bg-white` virou `<Table>`
 * canônico (rola dentro do próprio card, a página não estoura no celular);
 * "Camada 4 inviolável", "dry_run/live" e os status crus viraram texto em
 * português; `red-*` virou token.
 */
import { useEffect, useState } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';

import {
  Alert,
  Badge,
  Button,
  Card,
  EmptyState,
  Label,
  PageHeader,
  Select,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui';
import { api } from '@/lib/api';

import { formatDataHoraSegundos } from '@/lib/format-date';
interface AuditEntry {
  id: string;
  empresa_id: string;
  user_id: string | null;
  endpoint: string;
  http_status: number | null;
  omie_codigo_criado: string | null;
  codigo_integracao: string | null;
  modo: 'dry_run' | 'live';
  status: 'sucesso' | 'falha' | 'simulado';
  timestamp_iso: string;
  reversao_executada: number;
}

interface AuditResp {
  items: AuditEntry[];
  total: number;
  limit: number;
  offset: number;
}

/** Nome interno do modo → nome de tela. */
const MODO_LABEL: Record<AuditEntry['modo'], string> = {
  dry_run: 'Simulação',
  live: 'Envio real',
};

const STATUS_BADGE: Record<
  AuditEntry['status'],
  { label: string; variant: 'success' | 'danger' | 'warning' }
> = {
  sucesso: { label: 'Sucesso', variant: 'success' },
  falha: { label: 'Falha', variant: 'danger' },
  simulado: { label: 'Simulado', variant: 'warning' },
};

export function OmieAuditLogPage() {
  const [data, setData] = useState<AuditResp | null>(null);
  const [filterModo, setFilterModo] = useState<string>('');
  const [filterStatus, setFilterStatus] = useState<string>('');
  const [offset, setOffset] = useState(0);
  const [err, setErr] = useState<string | null>(null);
  const limit = 50;

  function load() {
    const params = new URLSearchParams();
    if (filterModo) params.set('modo', filterModo);
    if (filterStatus) params.set('status', filterStatus);
    params.set('limit', String(limit));
    params.set('offset', String(offset));
    setErr(null);
    api
      .get<AuditResp>(`/api/v1/admin/omie/audit-log?${params}`)
      .then(setData)
      .catch((e: Error) => setErr(e.message));
  }
  useEffect(load, [filterModo, filterStatus, offset]);

  return (
    <div className="min-w-0 space-y-5" data-testid="omie-audit-log-page">
      <PageHeader
        className="mb-0"
        crumb="Configurações · Integrações"
        title="Registro de envios ao Omie"
        subtitle="Toda tentativa de gravar algo no Omie é anotada aqui antes de acontecer — inclusive as simulações. Este registro não pode ser apagado nem alterado por ninguém."
      />

      <Card variant="flat" className="p-4">
        <div className="flex flex-wrap items-end gap-3">
          <div className="min-w-0">
            <Label htmlFor="audit-filtro-modo">Modo</Label>
            <Select
              id="audit-filtro-modo"
              size="sm"
              wrapperClassName="w-full sm:w-52"
              value={filterModo}
              onChange={(e) => {
                setFilterModo(e.target.value);
                setOffset(0);
              }}
              data-testid="filter-modo"
            >
              <option value="">Todos</option>
              <option value="dry_run">Simulação</option>
              <option value="live">Envio real</option>
            </Select>
          </div>
          <div className="min-w-0">
            <Label htmlFor="audit-filtro-status">Situação</Label>
            <Select
              id="audit-filtro-status"
              size="sm"
              wrapperClassName="w-full sm:w-52"
              value={filterStatus}
              onChange={(e) => {
                setFilterStatus(e.target.value);
                setOffset(0);
              }}
              data-testid="filter-status"
            >
              <option value="">Todas</option>
              <option value="sucesso">Sucesso</option>
              <option value="falha">Falha</option>
              <option value="simulado">Simulado</option>
            </Select>
          </div>
        </div>
      </Card>

      {err && (
        <Alert variant="danger" title="Não foi possível carregar o registro">
          Tente recarregar a página. Se continuar, avise quem cuida do servidor.
          <span className="mt-1 block break-words text-xs opacity-80">Detalhe: {err}</span>
        </Alert>
      )}

      {!data ? (
        <p className="text-sm text-ink-3 dark:text-ink-dark-3">Carregando…</p>
      ) : data.items.length === 0 ? (
        <Card variant="flat" data-testid="audit-empty">
          <EmptyState
            title="Nada registrado ainda"
            description="É o esperado enquanto o IGM só lê o Omie: o registro só recebe linhas quando o envio de dados sai do modo desligado."
          />
        </Card>
      ) : (
        <>
          <p className="text-sm text-ink-3 dark:text-ink-dark-3">
            {data.total} envio(s) no total — mostrando {data.items.length} a partir do nº{' '}
            {offset + 1}.
          </p>

          <Card variant="flat" className="min-w-0">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Quando</TableHead>
                  <TableHead>Empresa</TableHead>
                  <TableHead>O que foi chamado</TableHead>
                  <TableHead>Modo</TableHead>
                  <TableHead>Situação</TableHead>
                  <TableHead>Resposta</TableHead>
                  <TableHead>Código de integração</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {data.items.map((e) => (
                  <TableRow key={e.id}>
                    <TableCell className="whitespace-nowrap font-mono text-xs">
                      {formatDataHoraSegundos(e.timestamp_iso)}
                    </TableCell>
                    <TableCell className="text-xs">{e.empresa_id}</TableCell>
                    <TableCell className="break-words">{e.endpoint}</TableCell>
                    <TableCell>
                      <Badge variant="neutral" size="sm">
                        {MODO_LABEL[e.modo] ?? e.modo}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <Badge variant={STATUS_BADGE[e.status]?.variant ?? 'neutral'} size="sm">
                        {STATUS_BADGE[e.status]?.label ?? e.status}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-xs">{e.http_status ?? '—'}</TableCell>
                    <TableCell className="max-w-[220px] break-words font-mono text-xs">
                      {e.codigo_integracao ?? '—'}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Card>

          <div className="flex flex-wrap items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              leftIcon={<ChevronLeft className="h-4 w-4" />}
              onClick={() => setOffset(Math.max(0, offset - limit))}
              disabled={offset === 0}
            >
              Anteriores
            </Button>
            <Button
              variant="outline"
              size="sm"
              rightIcon={<ChevronRight className="h-4 w-4" />}
              onClick={() => setOffset(offset + limit)}
              disabled={offset + data.items.length >= data.total}
            >
              Próximos
            </Button>
          </div>
        </>
      )}
    </div>
  );
}
